<template>
  <div class="video-receiver">
    <div class="wrapper">
      <video class="player" :class="{'webcam-loading': !connections.webcam}" playsinline autoplay ref="webcam"></video>
      <span :class="{muted: isMuted}"></span>
    </div>
    <div v-if="desktop" class="wrapper" :class="{'show-maximize': connections.desktop, maximized: isMaximized}">
      <video class="player" :class="{'desktop-loading': !connections.desktop}" playsinline autoplay ref="desktop">
      </video>
      <span :class="isMaximized ? 'minimize' : 'maximize'" @click="toggleDesktopSize"></span>
    </div>
  </div>
</template>

<script>

import { mapState, mapActions } from 'vuex'

export default {
  name: 'VideoReceiver',
  props: {
    remoteClientId: String,
    token: String,
    desktop: Boolean,
  },
  data () {
    return {
      connection: null,
      connections: {
        webcam: false,
        desktop: false
      },
      isMaximized: false,
      isMuted: false
    }
  },
  mounted () {
    this.makeP2PClient().then(() => {
      this.connectToBroadcast(this.remoteClientId, this.token)
    })
  },
  beforeDestroy () {
    if (this.connection) {
      this.connection.stop()
    }
  },
  methods: {
    toggleDesktopSize () {
      this.isMaximized = !this.isMaximized
      const closeOnEscape = (evt) => {
        if (evt.code === 'Escape') {
          this.isMaximized = !this.isMaximized
          document.removeEventListener('keydown', closeOnEscape)
        }
      }
      document.addEventListener('keydown', closeOnEscape)
    },
    connectToBroadcast (remoteClientId, token) {
      this.connection = this.p2pClient.call(remoteClientId, token)
      this.connection.on('message', this.handleMessage)

      this.connection.on('mediaStream', ev => {
        const { name, mediaStream } = ev
        const videoEl = this.$refs[name]

        // check in case mediaStream is null
        if (mediaStream) {
          mediaStream.oninactive = () => {
            this.cleanupPlayer(videoEl, name)
          }
        }

        if (videoEl) {
          videoEl.srcObject = mediaStream
          this.connections[name] = true
        } else {
          console.log('TRACK WITHOUT NAME', ev.name)
        }
      })
    },
    cleanupPlayer (player, streamName) {
      player.srcObject = null
      this.connections[streamName] = false
    },
    handleMessage (data) {
      switch (data) {
        case 'muted':
          this.isMuted = true
          break
        case 'unmuted':
          this.isMuted = false
          break
      }
    },
    ...mapActions(['makeP2PClient']),
  },
  computed: mapState(['p2pClient'])
}
</script>

<style lang="scss" scoped>
@import "~@/styles/base.scss";

.player {
  width: 100%;
  min-height: 150px;

  background-color: $main-light;
  border-radius: $button-radius;

  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 150, 136, 0.6);
}

.video-receiver {
  display: flex;
  width: 100%;
}

.wrapper {
  display: flex;
  width: 100%;
  position: relative;
}

.show-maximize {
  &:hover > .player {
    opacity: 0.5;
  }

  &:hover > .maximize {
    visibility: visible;
  }
}

@mixin loading($icon-path) {
  background-image: url($icon-path);
  background-repeat: no-repeat;
  background-size: 30% 30%;
  background-position: 50% 50%;
}

.webcam-loading {
  @include loading("~@/assets/webcam-off-green.svg");
}

.desktop-loading {
  @include loading("~@/assets/desktop-off-green.svg");
}

.muted {
  position: absolute;
  width: 17px;
  height: 17px;
  bottom: 7px;
  left: 10px;
  padding: 5px;

  background-image: url('~@/assets/mic-off-white.svg');
  background-repeat: no-repeat;
  background-size: 17px;
  background-position: 50% 50%;
  background-color: #d65252;

  border-radius: 50%;
}

%desktop-size-toggle {
  display: block;
  position: absolute;
  width: 70px;
  height: 70px;

  background-size: 70px 70px;
  background-repeat: no-repeat;

  cursor: pointer;
}

.maximize {
  @extend %desktop-size-toggle;
  visibility: hidden;

  top: 50%;
  left: 50%;

  background-image: url("~@/assets/maximize-small.svg");
  transform: translate(-50%, -50%);

  &:hover {
    background-image: url("~@/assets/maximize.svg");
  }
}

.minimize {
  @extend %desktop-size-toggle;
  top: 30px;
  right: 50px;

  background-image: url("~@/assets/x-white.svg");

  &:hover {
    opacity: 0.8;
  }
}

.maximized {
  position: fixed;
  box-sizing: border-box;
  top: 0;
  left: 0;
  padding: 50px;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);

  &.show-maximize:hover > .player {
    opacity: unset;
  }

  .player {
    background-color: unset;
    box-shadow: unset;
    max-width: unset;
  }
}

</style>
